/**
 * AlertThresholdsPage — System · Alert Thresholds.
 *
 * Numeric thresholds that trip CTR / SAR / monitoring alerts on the RPM
 * dashboard. When a metric crosses its threshold the configured severity
 * decides who gets pinged in Teams. BSA/AML-sensitive: the dollar thresholds
 * (CTR $10,000, etc.) are regulatory minimums — set ThresholdValue + severity
 * deliberately.
 *
 * Contract (verified against the backend 2026-06-16 — entity, service, read
 * route, and admin write/delete routes all inspected):
 *   - Entity:  PCBJxChangeApi/Data/Entities/Config/Codes/AlertThreshold.cs
 *              (PK MetricKey; decimal ThresholdValue; HAS IsActive)
 *   - Lookup:  AlertThresholdCategory.cs (Code/Name/Description/IsActive/SortOrder)
 *   - Read:    GET /v3/config/alert-thresholds
 *              → V3ConfigFunction.AlertThresholds returns
 *                { success:true, data:{ thresholds, categories } }.
 *              Returns ALL rows (active AND inactive) — it does NOT filter
 *              WHERE IsActive. Ordered by CategoryCode, MetricKey.
 *   - Write:   POST /v3/admin/alert-thresholds
 *              → AdminNotificationsService.UpsertAlertThresholdAsync. Persists
 *              EVERY field on both create and update branches (MetricKey,
 *              DisplayName, CategoryCode, ThresholdValue, Unit, Severity,
 *              NotifyRoleCodes, IsActive) — no silent drop.
 *   - Delete:  DELETE /v3/admin/alert-thresholds/{metricKey} EXISTS and is a
 *              HARD EF-Remove. NOT used here: because the read surfaces inactive
 *              rows, hard-deleting would make a row vanish from a list that
 *              intentionally shows inactive entries (and lose audit history).
 *              Per the migrate rule (read does NOT filter WHERE IsActive →
 *              soft-delete), we DEACTIVATE via re-upsert isActive=false and
 *              keep IsActive editable in the form.
 *
 * Two confirmed wire drifts, both handled by NOT using the mistyped typed
 * helpers and going through the raw transport instead:
 *   1. config.alertThresholds() is declared
 *      Promise<ApiResult<ReadonlyArray<AlertThreshold>>> (array data) but the
 *      wire returns an OBJECT { thresholds, categories } — same trap as
 *      slaTargets(). Read via raw client.http.get with the correct envelope.
 *   2. No typed admin.alertThresholds write surface exists in api-client, so
 *      write/deactivate go through raw client.http.post.
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/alertThresholdsEditor.tsx — the
 * operational app writes no config). Field names + routes are the verbatim
 * verified wire contract.
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders omits the bypass-key
 * (MSAL bearer carries authority; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

type Severity = 'info' | 'warn' | 'critical';

interface ThresholdRow {
  metricKey: string;
  displayName: string;
  categoryCode: string;
  thresholdValue: number;
  unit: string | null;
  severity: string;
  notifyRoleCodes: string | null;
  isActive: boolean;
}

interface CategoryRow {
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number | null;
}

interface AlertThresholdsPayload {
  thresholds: ReadonlyArray<ThresholdRow>;
  categories: ReadonlyArray<CategoryRow>;
}

interface AdminWriteEnvelope {
  success: boolean;
  error?: string;
  code?: string;
}

interface UpsertBody {
  metricKey: string;
  displayName: string;
  categoryCode: string;
  thresholdValue: number;
  unit: string | null;
  severity: string;
  notifyRoleCodes: string | null;
  isActive: boolean;
}

interface EditDraft extends ThresholdRow {
  isNew: boolean;
}

const SEVERITY_OPTIONS: ReadonlyArray<{ value: Severity; label: string }> = [
  { value: 'info', label: 'Info — logged only, no Teams ping' },
  { value: 'warn', label: 'Warning — Teams notification to subscribed roles' },
  { value: 'critical', label: 'Critical — red alert, pages on-call' },
];

const COMMON_UNITS: ReadonlyArray<string> = ['count', 'usd', 'percent', 'minutes', 'hours', 'days'];

const METRIC_KEY_PATTERN = /^[A-Za-z0-9_.]{1,64}$/;

function emptyDraft(firstCategory: string): EditDraft {
  return {
    metricKey: '',
    displayName: '',
    categoryCode: firstCategory,
    thresholdValue: 0,
    unit: 'count',
    severity: 'warn',
    notifyRoleCodes: '',
    isActive: true,
    isNew: true,
  };
}

function rowToDraft(r: ThresholdRow): EditDraft {
  return { ...r, isNew: false };
}

function draftToBody(d: EditDraft): UpsertBody {
  const unit = d.unit && d.unit.trim() ? d.unit.trim() : null;
  const notify = d.notifyRoleCodes && d.notifyRoleCodes.trim() ? d.notifyRoleCodes.trim() : null;
  return {
    metricKey: d.metricKey.trim(),
    displayName: d.displayName.trim(),
    categoryCode: d.categoryCode.trim(),
    thresholdValue: d.thresholdValue,
    unit,
    severity: d.severity,
    notifyRoleCodes: notify,
    isActive: d.isActive,
  };
}

function severityPill(s: string): JSX.Element {
  if (s === 'critical') return <Pill tone="err">critical</Pill>;
  if (s === 'warn') return <Pill tone="warn">warning</Pill>;
  return <Pill tone="info">info</Pill>;
}

export function AlertThresholdsPage(): JSX.Element {
  const client = useApiClient();
  const [data, setData] = useState<AlertThresholdsPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ThresholdRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setData(null);
      setLoadError(null);
      try {
        // The typed config.alertThresholds() is mis-typed as ApiResult<Array>;
        // the wire actually returns { thresholds, categories }. Read raw with
        // the correct envelope shape.
        const r = await client.http.get<{
          success: boolean;
          data?: AlertThresholdsPayload;
          error?: string;
        }>('/v3/config/alert-thresholds');
        if (cancelled) return;
        if (r.success && r.data && Array.isArray(r.data.thresholds)) {
          setData({
            thresholds: r.data.thresholds,
            categories: Array.isArray(r.data.categories) ? r.data.categories : [],
          });
        } else {
          setLoadError(r.error ?? 'Failed to load alert thresholds.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const categoryByCode = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of data?.categories ?? []) m.set(c.code, c);
    return m;
  }, [data]);

  const categoryOptions = useMemo(
    () =>
      (data?.categories ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
    [data],
  );

  const visible = useMemo(() => {
    const sorted = (data?.thresholds ?? []).slice().sort((a, b) => {
      if (a.categoryCode !== b.categoryCode) return a.categoryCode.localeCompare(b.categoryCode);
      return a.metricKey.localeCompare(b.metricKey);
    });
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((t) => {
      const cat = categoryByCode.get(t.categoryCode);
      const hay = `${t.metricKey} ${t.displayName} ${cat?.name ?? t.categoryCode} ${t.severity}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [data, filter, categoryByCode]);

  const upsert = useCallback(
    async (body: UpsertBody): Promise<void> => {
      // No typed admin.alertThresholds surface in api-client — raw POST.
      const r = await client.http.post<AdminWriteEnvelope>('/v3/admin/alert-thresholds', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body,
      });
      if (!r.success) throw new Error(r.error ?? 'admin upsert returned success=false');
    },
    [client],
  );

  const columns: ReadonlyArray<DataTableColumn<ThresholdRow>> = useMemo(
    () => [
      {
        header: 'Metric key',
        cell: (r) => <code className="cell-mono--bold">{r.metricKey}</code>,
      },
      {
        header: 'Display name',
        cell: (r) => <span className="cell-strong">{r.displayName}</span>,
      },
      {
        header: 'Category',
        cell: (r) => (
          <span className="cell-desc">{categoryByCode.get(r.categoryCode)?.name ?? r.categoryCode}</span>
        ),
      },
      {
        header: 'Threshold',
        cell: (r) => (
          <span className="num">
            {r.thresholdValue}
            {r.unit ? <span className="cell-desc"> {r.unit}</span> : null}
          </span>
        ),
      },
      { header: 'Severity', cell: (r) => severityPill(r.severity) },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [categoryByCode],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const key = draft.metricKey.trim();
    if (!key || !METRIC_KEY_PATTERN.test(key)) {
      setSaveError('Metric key must be 1–64 chars (letters, digits, underscore, dot).');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Display name is required.');
      return;
    }
    if (!draft.categoryCode.trim()) {
      setSaveError('Pick a category.');
      return;
    }
    if (!Number.isFinite(draft.thresholdValue)) {
      setSaveError('Threshold value must be a number.');
      return;
    }
    if (!draft.severity.trim()) {
      setSaveError('Pick a severity.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draftToBody(draft));
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew ? `Created alert threshold "${key}".` : `Updated alert threshold "${key}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleDeactivate = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-delete: re-upsert with isActive=false. The read surfaces inactive
      // rows, so we deactivate (rather than hard-DELETE) to keep the audit
      // trail and let an admin flip it back on.
      await upsert(draftToBody({ ...deleteTarget, isActive: false, isNew: false }));
      setToast({
        tone: 'warn',
        text: `${deleteTarget.metricKey} is now inactive. Alerts for this metric stop firing.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load alert thresholds: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!data) return <Spinner label="Loading alert thresholds…" />;

  const noCategories = categoryOptions.length === 0;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        These thresholds trip CTR / SAR / monitoring alerts on the RPM dashboard. Setting one too
        low floods the team with false positives; too high and a real problem flies under the radar.
        Treat the dollar thresholds (CTR $10,000, etc.) as regulatory minimums and change them only
        with BSA/compliance sign-off.
      </Banner>

      <Card
        title={`Alert Thresholds — ${visible.length} of ${data.thresholds.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="key, name, category, or severity…"
              />
            </div>
            <Button
              variant="primary"
              disabled={noCategories}
              onClick={() => { setDraft(emptyDraft(categoryOptions[0]?.value ?? '')); setSaveError(null); }}
            >
              + Add Threshold
            </Button>
          </div>
        }
      >
        {noCategories && (
          <Banner tone="warn">
            No active alert-threshold categories exist yet. Seed a category before adding thresholds.
          </Banner>
        )}
        <DataTable<ThresholdRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.metricKey}
          emptyState={<p className="empty">No alert thresholds match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add an alert threshold' : `Edit ${draft?.metricKey ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Metric key"
                value={draft.metricKey}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({ ...draft, metricKey: v.replace(/[^A-Za-z0-9_.]/g, '').slice(0, 64) })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. ctr.daily.amount"
                hint={draft.isNew ? 'Up to 64 chars, dot-separated. Permanent once saved.' : 'Permanent — key cannot change.'}
              />
              <TextField
                label="Display name"
                value={draft.displayName}
                onChange={(v) => setDraft({ ...draft, displayName: v.slice(0, 128) })}
                placeholder="e.g. Daily CTR Dollar Amount"
                hint="Shown on the RPM dashboard alert."
              />
            </div>

            <div className="form-row">
              <SelectField
                label="Category"
                value={draft.categoryCode}
                onChange={(v) => setDraft({ ...draft, categoryCode: v })}
                options={categoryOptions}
                placeholder="— Select category —"
                hint="Groups the threshold on the dashboard. Sourced from AlertThresholdCategories."
              />
              <SelectField
                label="Severity"
                value={draft.severity}
                onChange={(v) => setDraft({ ...draft, severity: v })}
                options={SEVERITY_OPTIONS}
                hint="Drives who gets pinged when the threshold trips."
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Threshold value"
                value={draft.thresholdValue}
                onChange={(v) => setDraft({ ...draft, thresholdValue: v ?? 0 })}
                step={0.01}
                placeholder="10000"
                hint="Stored as a precise decimal. For CTR use the regulatory minimum (10000)."
              />
              <SelectField
                label="Unit"
                value={draft.unit ?? ''}
                onChange={(v) => setDraft({ ...draft, unit: v || null })}
                options={[
                  ...COMMON_UNITS.map((u) => ({ value: u, label: u })),
                  ...(draft.unit && !COMMON_UNITS.includes(draft.unit)
                    ? [{ value: draft.unit, label: `${draft.unit} (custom)` }]
                    : []),
                ]}
                placeholder="— None —"
                hint="USD, count, percent, minutes…"
              />
            </div>

            <TextField
              label="Notify role codes"
              value={draft.notifyRoleCodes ?? ''}
              onChange={(v) => setDraft({ ...draft, notifyRoleCodes: v || null })}
              placeholder="Compliance, BSA, Supervisor"
              hint="Comma-separated role codes to ping in Teams when the threshold trips. Optional."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive thresholds stop firing alerts but stay visible for audit. Flip back on anytime."
            />

            <Banner tone="info">
              When <strong>{draft.displayName || draft.metricKey || '—'}</strong> crosses{' '}
              <strong>
                {draft.thresholdValue}
                {draft.unit ? ` ${draft.unit}` : ''}
              </strong>
              , raise a <strong>{draft.severity}</strong> alert
              {draft.notifyRoleCodes && draft.notifyRoleCodes.trim()
                ? ` and notify ${draft.notifyRoleCodes.trim()}.`
                : '.'}
            </Banner>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this threshold inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDeactivate()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark <code className="cell-mono">{deleteTarget.metricKey}</code> (
              {deleteTarget.displayName}) inactive. Alerts for this metric stop firing immediately,
              but the row stays for audit and you can flip it back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default AlertThresholdsPage;
